import * as Api from '@ViewModels';
import * as React from 'react';
import { useExportNotesMutation, useExportPostsMutation, useExportTextingReportMutation } from '../../../../../queries';
import { ExportConfirmationModalV2 } from '../../../../../web/components/ExportConfirmation';
import { ITextExportDateRange, TextExportDateRangeDialog } from './TextExportDateRangeDialog';

export enum ExportDataType {
	Notes,
	Posts,
	Texts,
}

export interface IExportDataButtonProps {
	exportDataType: ExportDataType;
	text: string;
	impersonationContext: Api.IImpersonationContext;
	className?: string;
	disabled?: boolean;
}

export const ExportDataButton: React.FC<IExportDataButtonProps> = props => {
	const [isTextDateRangeDialogOpen, setIsTextDateRangeDialogOpen] = React.useState(false);

	const onRequestClose = () => {
		exportTextingReportMutation.reset();
		exportNotesMutation.reset();
		exportPostsMutation.reset();
	};

	const exportTextingReportMutation = useExportTextingReportMutation({
		impersonationContext: props.impersonationContext,
	});

	const exportPostsMutation = useExportPostsMutation({
		impersonationContext: props.impersonationContext,
	});

	const exportNotesMutation = useExportNotesMutation({
		impersonationContext: props.impersonationContext,
	});

	const onExportClick = () => {
		switch (props.exportDataType) {
			case ExportDataType.Notes:
				exportNotesMutation.mutate({ exportRequest: {} });
				break;
			case ExportDataType.Texts:
				setIsTextDateRangeDialogOpen(true);
				break;
			case ExportDataType.Posts:
				exportPostsMutation.mutate();
				break;
			default:
				break;
		}
	};

	const onExportTexts = ({ startDate, endDate }: ITextExportDateRange) => {
		setIsTextDateRangeDialogOpen(false);
		const hasDateRange = !!startDate || !!endDate;
		exportTextingReportMutation.mutate({
			// When a date range is scoped, includeAll must be false so the backend applies StartDate/EndDate.
			// With no dates, keep includeAll=true to export the account's entire text history (previous behavior).
			includeAll: !hasDateRange,
			startDate,
			endDate,
			filter: { criteria: [{ property: Api.TextMessageFilterProperty.All }] },
		});
	};

	return (
		<>
			<button className={props.className} onClick={onExportClick} disabled={props.disabled}>
				{props.text}
			</button>

			<TextExportDateRangeDialog
				isOpen={isTextDateRangeDialogOpen}
				onClose={() => setIsTextDateRangeDialogOpen(false)}
				onExport={onExportTexts}
			/>

			<ExportConfirmationModalV2
				mutationResult={exportTextingReportMutation}
				modalProps={{
					isOpen: !exportTextingReportMutation.isIdle,
					onRequestClose,
				}}
			/>

			<ExportConfirmationModalV2
				mutationResult={exportNotesMutation}
				modalProps={{
					isOpen: !exportNotesMutation.isIdle,
					onRequestClose,
				}}
			/>

			<ExportConfirmationModalV2
				mutationResult={exportPostsMutation}
				modalProps={{
					isOpen: !exportPostsMutation.isIdle,
					onRequestClose,
				}}
			/>
		</>
	);
};
